import React from "react";
import { InlineMath } from "react-katex";
import InteractivePointQuiz from "../../../../components/GraphPlotter/tasks/InteractivePointQuiz";

export const hideCorrectAnswerWhenUserAnswer = true;

const getQuarter = (x, y) => {
  if (x === 0 && y === 0) return "w środku układu współrzędnych";
  if (x === 0) return "na osi OY";
  if (y === 0) return "na osi OX";
  if (x > 0 && y > 0) return "w I ćwiartce";
  if (x < 0 && y > 0) return "w II ćwiartce";
  if (x < 0 && y < 0) return "w III ćwiartce";
  if (x > 0 && y < 0) return "w IV ćwiartce";
  return "";
};

const classifyPointUi = (x, y) => {
  if (x === 0 && y === 0) return "W początku układu";
  if (x === 0 && y > 0) return "Na dodatniej części osi Y";
  if (x === 0 && y < 0) return "Na ujemnej części osi Y";
  if (y === 0 && x > 0) return "Na dodatniej części osi X";
  if (y === 0 && x < 0) return "Na ujemnej części osi X";
  if (x > 0 && y > 0) return "I ćwiartka";
  if (x < 0 && y > 0) return "II ćwiartka";
  if (x < 0 && y < 0) return "III ćwiartka";
  if (x > 0 && y < 0) return "IV ćwiartka";
  return "";
};

function ExercisePointInput({ task }) {
  const { x, y, label, descriptionUi } = task.data;
  const [value, setValue] = React.useState({
    point: null,
    location: null,
    _target: { x, y },
    _correctLocation: descriptionUi,
    _label: label,
  });

  const handleChange = (payload) => {
    setValue({
      ...(payload && typeof payload === "object" ? payload : {}),
      _target: { x, y },
      _correctLocation: descriptionUi,
      _label: label,
    });
  };

  return (
    <InteractivePointQuiz
      targetPoint={{ x, y }}
      index={label.charCodeAt(0) - 65}
      value={value}
      onChange={handleChange}
      showPrompt={false}
    />
  );
}

export function generateTasks() {
  const labels = ["A", "B", "C", "D"];
  return labels.map((label) => {
    const x = Math.floor(Math.random() * 11) - 5;
    const y = Math.floor(Math.random() * 11) - 5;

    return {
      type: "InteractivePointTask",
      data: {
        label,
        x,
        y,
        descriptionPretty: getQuarter(x, y),
        descriptionUi: classifyPointUi(x, y),
      },
    };
  });
}

export function renderTask({ data }) {
  const { label, x, y } = data;
  return (
    <div>
      <p>
        Zaznacz na układzie współrzędnych, gdzie leży punkt{" "}
        <InlineMath math={`${label} = \\left(${x},\\ ${y}\\right)`} /> i określ jego położenie.
      </p>
    </div>
  );
}

export function renderExerciseTask(task) {
  return (
    <div>
      {renderTask(task)}
      <ExercisePointInput task={task} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const { x, y, label, descriptionUi } = task.data;

  const wrappedOnChange = (payload) => {
    onChange({
      ...(payload && typeof payload === "object" ? payload : {}),
      _target: { x, y },
      _correctLocation: descriptionUi,
      _label: label,
    });
  };

  return (
    <InteractivePointQuiz
      targetPoint={{ x, y }}
      index={label.charCodeAt(0) - 65}
      value={
        userAnswer || {
          point: null,
          location: null,
          _target: { x, y },
          _correctLocation: descriptionUi,
          _label: label,
        }
      }
      onChange={wrappedOnChange}
      locked={disabled}
      showPrompt={false}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { x, y, descriptionUi } = data;

  const p = userAnswer?.point;
  const hasPoint = p && typeof p.x === "number" && typeof p.y === "number";

  const pointCorrect = !!(hasPoint && p.x === x && p.y === y);
  const locationCorrect = userAnswer?.location === descriptionUi;
  const score = (pointCorrect ? 1 : 0) + (locationCorrect ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      Punkt: pointCorrect,
      "Położenie": locationCorrect,
    },
  };
}

export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const taskData = task?.data || {};
  const target =
    userAnswer._target ||
    (typeof taskData.x === "number" && typeof taskData.y === "number"
      ? { x: taskData.x, y: taskData.y }
      : null);
  const correctLocation = userAnswer._correctLocation || taskData.descriptionUi;
  const label = userAnswer._label || taskData.label || "A";

  const p = userAnswer.point;
  const hasPoint = p && typeof p.x === "number" && typeof p.y === "number";

  const hasTarget = target && typeof target.x === "number" && typeof target.y === "number";
  if (!hasTarget) {
    return (
      <div>
        <div>
          <strong>Punkt:</strong>{" "}
          {hasPoint ? <InlineMath math={`\\left(${p.x},\\ ${p.y}\\right)`} /> : <i>(nie zaznaczono)</i>}
        </div>
        <div>
          <strong>Położenie:</strong> {userAnswer.location ? userAnswer.location : <i>(nie wybrano)</i>}
        </div>
      </div>
    );
  }

  const pointCorrect = !!(hasPoint && p.x === target.x && p.y === target.y);
  const locationCorrect = userAnswer.location === correctLocation;
  const score = (pointCorrect ? 1 : 0) + (locationCorrect ? 1 : 0);
  const correctPretty = getQuarter(target.x, target.y);

  return (
    <div>
      <div style={{ marginBottom: 10, lineHeight: 1.35 }}>
        <div style={{ marginTop: 6 }}>
          <div>
            <strong>Punkt:</strong>{" "}
            {hasPoint ? <InlineMath math={`\\left(${p.x},\\ ${p.y}\\right)`} /> : <i>(nie zaznaczono)</i>}
          </div>

          <div>
            <strong>Położenie:</strong> {userAnswer.location ? userAnswer.location : <i>(nie wybrano)</i>}
          </div>

          {!hasPoint && (
            <div style={{ marginTop: 6 }}>
              <i>(brak zaznaczenia - pokazuję poprawny punkt na zielono)</i>
            </div>
          )}
        </div>

        {score < 2 && (
          <div style={{ marginTop: 10 }}>
            <strong>Poprawna odpowiedź:</strong>
            <div style={{ marginTop: 4 }}>
              Punkt <InlineMath math={`${label} = \\left(${target.x},\\ ${target.y}\\right)`} /> leży{" "}
              <strong>{correctPretty}</strong>.
            </div>
          </div>
        )}
      </div>

      <InteractivePointQuiz
        targetPoint={{ x: target.x, y: target.y }}
        index={label.charCodeAt(0) - 65}
        value={userAnswer}
        locked={true}
        showPrompt={false}
        validation={{ isCorrect: pointCorrect, correctPoint: { x: target.x, y: target.y } }}
      />
    </div>
  );
}

export function renderAnswer({ data }) {
  const { label, x, y, descriptionPretty } = data;

  return (
    <div>
      <p>
        Punkt <InlineMath math={`${label} = \\left(${x},\\ ${y}\\right)`} /> leży{" "}
        <strong>{descriptionPretty}</strong>.
      </p>

      <div style={{ marginTop: 10 }}>
        <InteractivePointQuiz
          targetPoint={{ x, y }}
          index={label.charCodeAt(0) - 65}
          value={{ point: null, location: null }}
          locked={true}
          showPrompt={false}
          hideLocationSelect={true}
          validation={{ isCorrect: true, correctPoint: { x, y } }}
        />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { x, y } = data;

  if (x === 0 && y === 0) {
    return (
      <p>
        Punkt leży <strong>w środku układu współrzędnych</strong>, ponieważ{" "}
        <InlineMath math={"x = 0 \\land y = 0"} />.
      </p>
    );
  }

  if (x === 0) {
    const direction = y > 0 ? "górnej części osi OY" : "dolnej części osi OY";
    const ySign = y > 0 ? "> 0" : "< 0";
    return (
      <p>
        Punkt leży <strong>{direction}</strong>, ponieważ{" "}
        <InlineMath math={`x = 0 \\land y ${ySign}`} />.
      </p>
    );
  }

  if (y === 0) {
    const direction = x > 0 ? "prawej części osi OX" : "lewej części osi OX";
    const xSign = x > 0 ? "> 0" : "< 0";
    return (
      <p>
        Punkt leży <strong>{direction}</strong>, ponieważ{" "}
        <InlineMath math={`y = 0 \\land x ${xSign}`} />.
      </p>
    );
  }

  const cw = getQuarter(x, y);
  const xSign = x > 0 ? "> 0" : "< 0";
  const ySign = y > 0 ? "> 0" : "< 0";

  return (
    <p>
      Ponieważ <InlineMath math={`x ${xSign} \\land y ${ySign}`} />, punkt leży <strong>{cw}</strong>.
    </p>
  );
}

export const registry = {
  InteractivePointTask: {
    hideCorrectAnswerWhenUserAnswer,
    renderTask,
    renderExerciseTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer: (userAnswer, task) => renderUserAnswer(userAnswer, task),
    renderExplanation,
  },
};